/**
 * PlayerContext
 *
 * Global playback state for the web player (HTML5 video + Cloudflare Stream HLS).
 *
 * Responsibilities:
 *  - Track which content/episode is currently loaded
 *  - Hold the signed HLS stream URL and its expiry
 *  - Track playback position for resume functionality
 *  - Provide play/pause/stop/seek actions to any component
 *
 * DECISION: Progress is stored in React state only — persistence to the
 * backend (watch_history) is handled by the player component via a debounced
 * POST to /content/:id/stream (which records watch_history as a side effect).
 * PlayerContext does not make API calls.
 *
 * DECISION: Mini-player (picture-in-picture) state lives here so the nav bar
 * can render a mini-player while the user browses elsewhere.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { ContentDetail, Episode } from '@/api/content'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PlayerTrack {
  /** The content being played */
  content: ContentDetail
  /** The specific episode, or null for films/documentaries */
  episode: Episode | null
  /** Signed Cloudflare Stream HLS URL */
  streamUrl: string
  /** ISO timestamp when the stream URL expires */
  streamExpiresAt: string
}

export interface PlayerState {
  /** Currently loaded track, or null if player is idle */
  track: PlayerTrack | null

  /** Whether playback is active */
  isPlaying: boolean

  /** Current position in seconds */
  currentTime: number

  /** Total duration in seconds (set once video metadata loads) */
  duration: number

  /** Volume 0–1 */
  volume: number

  /** Muted state */
  isMuted: boolean

  /** Whether the player is rendered in mini (PiP) mode */
  isMiniPlayer: boolean

  /** True while the stream URL is being fetched */
  isLoading: boolean

  /** Any stream error message */
  error: string | null
}

interface PlayerContextValue extends PlayerState {
  /** Load a new track and start playing */
  play: (track: PlayerTrack) => void

  /** Toggle play/pause */
  togglePlay: () => void

  /** Seek to a specific second */
  seek: (seconds: number) => void

  /** Update current playback position (called by the video element's onTimeUpdate) */
  updateTime: (seconds: number) => void

  /** Set the total duration once video metadata loads */
  setDuration: (seconds: number) => void

  /** Set volume (0–1) */
  setVolume: (v: number) => void

  /** Toggle mute */
  toggleMute: () => void

  /** Collapse to mini-player */
  minimise: () => void

  /** Expand from mini-player back to full player */
  expand: () => void

  /** Stop playback and clear the track */
  stop: () => void

  /** Set loading state (used by player component while fetching stream URL) */
  setLoading: (loading: boolean) => void

  /** Set stream error */
  setError: (message: string | null) => void
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_STATE: PlayerState = {
  track: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  isMiniPlayer: false,
  isLoading: false,
  error: null,
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PlayerContext = createContext<PlayerContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlayerState>(DEFAULT_STATE)

  const play = useCallback((track: PlayerTrack) => {
    setState((prev) => ({
      ...prev,
      track,
      isPlaying: true,
      currentTime: 0,
      duration: 0,
      isMiniPlayer: false,
      error: null,
    }))
  }, [])

  const togglePlay = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }))
  }, [])

  const seek = useCallback((seconds: number) => {
    setState((prev) => ({ ...prev, currentTime: seconds }))
  }, [])

  const updateTime = useCallback((seconds: number) => {
    setState((prev) => ({ ...prev, currentTime: seconds }))
  }, [])

  const setDuration = useCallback((seconds: number) => {
    setState((prev) => ({ ...prev, duration: seconds }))
  }, [])

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v))
    setState((prev) => ({ ...prev, volume: clamped, isMuted: clamped === 0 }))
  }, [])

  const toggleMute = useCallback(() => {
    setState((prev) => ({ ...prev, isMuted: !prev.isMuted }))
  }, [])

  const minimise = useCallback(() => {
    setState((prev) => ({ ...prev, isMiniPlayer: true }))
  }, [])

  const expand = useCallback(() => {
    setState((prev) => ({ ...prev, isMiniPlayer: false }))
  }, [])

  const stop = useCallback(() => {
    setState(DEFAULT_STATE)
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }))
  }, [])

  const setError = useCallback((message: string | null) => {
    setState((prev) => ({ ...prev, error: message, isLoading: false }))
  }, [])

  const value: PlayerContextValue = {
    ...state,
    play,
    togglePlay,
    seek,
    updateTime,
    setDuration,
    setVolume,
    toggleMute,
    minimise,
    expand,
    stop,
    setLoading,
    setError,
  }

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext)
  if (!ctx) {
    throw new Error('usePlayer must be used within a <PlayerProvider>')
  }
  return ctx
}

// ─── Convenience selectors ────────────────────────────────────────────────────

/** Returns true if the track is a series episode (not a standalone film) */
export function useIsEpisode(): boolean {
  const { track } = usePlayer()
  return track?.episode !== null && track?.episode !== undefined
}

/** Returns progress percentage (0–100) */
export function useProgressPercent(): number {
  const { currentTime, duration } = usePlayer()
  if (!duration) return 0
  return Math.min(100, (currentTime / duration) * 100)
}
